import { useRef, useState } from 'react';
import { Button, Dialog, Field, Input, ProgressBar, Select, Textarea } from '../design';
import { ApiError } from '../lib/api';
import { audioApi, formatBytes, uploadAudio, type AudioUpdateInput } from '../lib/audio';
import { AUDIO_KIND_LABELS, type AudioItem, type AudioKind } from '../lib/types';

/**
 * Create (multipart upload with progress) and edit (metadata PATCH) for catalog
 * programs. One dialog, two modes — the screen already carries two role views
 * plus the player, so these live out here (CalEmbed precedent).
 */

const MAX_BYTES = 1024 * 1024 * 1024; // must match AUDIO_MAX_BYTES server-side

interface ProgramDialogProps {
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  file?: AudioItem;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function ProgramDialog({ mode, file, onClose, onSaved }: ProgramDialogProps) {
  const [picked, setPicked] = useState<File | null>(null);
  const [title, setTitle] = useState(file?.title ?? '');
  const [kind, setKind] = useState<AudioKind>(file?.kind ?? 'audible');
  const [description, setDescription] = useState(file?.description ?? '');
  const [durationMin, setDurationMin] = useState(
    file?.durationSec ? String(Math.round(file.durationSec / 60)) : '',
  );
  const [wooProductId, setWooProductId] = useState(file?.wooProductId ?? '');
  const [storeUrl, setStoreUrl] = useState(file?.storeUrl ?? '');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function pick(f: File | null) {
    setPicked(f);
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop();
    // Kind defaults from the extension but stays editable — an audible WAV is
    // legitimate; an ultrasonic MP3 is not (server enforces).
    if (ext === 'wav') setKind('ultrasonic');
    else setKind('audible');
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  function validate(): string | null {
    if (mode === 'create') {
      if (!picked) return 'Choose an audio file.';
      const ext = picked.name.toLowerCase().split('.').pop();
      if (ext !== 'mp3' && ext !== 'wav') return 'Only .mp3 and .wav files are accepted.';
      if (picked.size > MAX_BYTES) return 'File is too large (1 GB max).';
      if (kind === 'ultrasonic' && ext !== 'wav') return 'Ultrasonic programs must be WAV files.';
    }
    if (!title.trim()) return 'Title is required.';
    if (durationMin && (!/^\d+$/.test(durationMin) || Number(durationMin) <= 0)) {
      return 'Duration must be a number of minutes.';
    }
    if (wooProductId && !/^\d+$/.test(wooProductId.trim())) return 'Woo product ID must be a number.';
    if (storeUrl && !/^https:\/\/\S+$/.test(storeUrl.trim())) return 'Store URL must be an https:// link.';
    return null;
  }

  async function submit() {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      if (mode === 'create' && picked) {
        const { promise, abort } = uploadAudio(
          {
            title: title.trim(),
            kind,
            description: description.trim() || undefined,
            durationSec: durationMin ? String(Number(durationMin) * 60) : undefined,
            wooProductId: wooProductId.trim() || undefined,
            storeUrl: storeUrl.trim() || undefined,
          },
          picked,
          (loaded, total) => setProgress({ loaded, total }),
        );
        abortRef.current = abort;
        await promise;
        onSaved(`"${title.trim()}" uploaded — publish it when it's ready.`);
      } else if (file) {
        const patch: AudioUpdateInput = {};
        if (title.trim() !== file.title) patch.title = title.trim();
        if ((description.trim() || null) !== file.description) patch.description = description.trim() || null;
        if (kind !== file.kind) patch.kind = kind;
        const newDuration = durationMin ? Number(durationMin) * 60 : null;
        if (newDuration !== file.durationSec) patch.durationSec = newDuration;
        if ((wooProductId.trim() || null) !== (file.wooProductId ?? null)) patch.wooProductId = wooProductId.trim() || null;
        if ((storeUrl.trim() || null) !== file.storeUrl) patch.storeUrl = storeUrl.trim() || null;
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        await audioApi.update(file.id, patch);
        onSaved(`"${title.trim()}" updated.`);
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Something went wrong.');
      setSaving(false);
      setProgress(null);
      abortRef.current = null;
      return;
    }
    setSaving(false);
  }

  function close() {
    if (saving && progress) {
      if (!window.confirm('Cancel this upload?')) return;
      abortRef.current?.();
    }
    onClose();
  }

  const pct = progress ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
    <Dialog
      open
      title={mode === 'create' ? 'New program' : 'Edit program'}
      onClose={close}
      width={520}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={close}>
            {saving && progress ? 'Cancel upload' : 'Cancel'}
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={saving}>
            {saving ? (mode === 'create' ? 'Uploading…' : 'Saving…') : mode === 'create' ? 'Upload' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {mode === 'create' && (
          <Field label="Audio file" hint="MP3 or WAV, up to 1 GB. Ultrasonic programs must be WAV.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.wav,audio/mpeg,audio/wav"
                style={{ display: 'none' }}
                onChange={(e) => pick(e.target.files?.[0] ?? null)}
              />
              <Button variant="secondary" size="sm" icon="upload" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                Choose file
              </Button>
              <span style={{ font: 'var(--type-body-sm)', color: picked ? 'var(--text-strong)' : 'var(--text-muted)' }}>
                {picked ? `${picked.name} · ${formatBytes(picked.size)}` : 'No file chosen'}
              </span>
            </div>
          </Field>
        )}
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={saving} placeholder="Deep Sleep" />
        </Field>
        <Field label="Kind" hint="Ultrasonic programs are silent by design and delivered as WAV.">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as AudioKind)}
            disabled={saving}
            options={(Object.keys(AUDIO_KIND_LABELS) as AudioKind[]).map((k) => ({ value: k, label: AUDIO_KIND_LABELS[k] }))}
          />
        </Field>
        <Field label="Description">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
          <Field label="Duration (minutes)">
            <Input value={durationMin} onChange={(e) => setDurationMin(e.target.value)} disabled={saving} placeholder="30" />
          </Field>
          <Field label="Woo product ID" hint="From the store's product admin.">
            <Input value={wooProductId} onChange={(e) => setWooProductId(e.target.value)} disabled={saving} placeholder="101" />
          </Field>
        </div>
        <Field label="Store URL" hint="Where locked cards send clients to buy.">
          <Input value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} disabled={saving} placeholder="https://zenmindware.com/product/…" />
        </Field>
        {progress && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <ProgressBar value={progress.loaded} max={progress.total} />
            <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
              Uploading… {pct}% · {formatBytes(progress.loaded)} of {formatBytes(progress.total)}
            </span>
          </div>
        )}
        {formError && <span style={{ color: 'var(--status-danger)', font: 'var(--type-body-sm)' }}>{formError}</span>}
      </div>
    </Dialog>
  );
}
